import { Camera, X } from 'lucide-react'
import { useState, type FormEvent, type ReactNode } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { CameraScanner } from './camera-scanner'

/**
 * The one scan control that serves every hardware profile a Point has (spec §1,
 * ADR-0010): a USB scanner behaving as a keyboard wedge, the phone camera, and
 * typing a code by hand. All three converge on a **single funnel** — `submit` —
 * so whatever a Point's hardware is, a scan reaches exactly one `onScan` call.
 * That funnel is the seam this control is tested at.
 *
 * A keyboard-wedge scanner types the code and appends Enter, so it and manual
 * typing are the same text field submitted; the camera path feeds its decoded
 * value into the same funnel. This control does not itself resolve the code —
 * resolution is the server's total resolver (ADR-0001), reached by whatever
 * `onScan` does — so it stays a reusable input, not a screen.
 */

export interface ScanInputProps {
	/** Called once per scan with the raw code, from any of the three input paths. */
	onScan: (code: string) => void
	/**
	 * Test seam: renders the camera scanner. Production uses the real
	 * `CameraScanner`; a test injects a fake and drives `onResult` to simulate a
	 * camera *read* without real hardware (spec Testing Decisions).
	 */
	renderCamera?: (props: {
		onResult: (code: string) => void
		onClose: () => void
	}) => ReactNode
	disabled?: boolean
}

export function ScanInput({ onScan, renderCamera, disabled }: ScanInputProps) {
	const { t } = useTranslate()
	const [value, setValue] = useState('')
	const [cameraOpen, setCameraOpen] = useState(false)

	// The single funnel: keyboard-wedge input, manual typing and a camera read
	// all arrive here, so all three produce the same resolution call.
	const submit = (raw: string) => {
		const code = raw.trim()
		if (!code) return
		onScan(code)
		setValue('')
		setCameraOpen(false)
	}

	const handleFormSubmit = (event: FormEvent) => {
		event.preventDefault()
		submit(value)
	}

	const camera = renderCamera ?? ((props) => <CameraScanner {...props} />)

	return (
		<div className='flex flex-col gap-2'>
			<form
				className='flex items-center gap-2'
				onSubmit={handleFormSubmit}
				data-testid='scan-form'
			>
				<Input
					data-testid='scan-input'
					autoFocus
					value={value}
					disabled={disabled}
					placeholder={t('parcels.scanPlaceholder')}
					aria-label={t('parcels.scanPlaceholder')}
					onChange={(event) => setValue(event.target.value)}
				/>
				<Button type='submit' data-testid='scan-submit' disabled={disabled}>
					{t('parcels.scanSubmit')}
				</Button>
				<Button
					type='button'
					variant='outline'
					size='icon'
					data-testid='scan-camera-toggle'
					aria-label={t('parcels.scanCamera')}
					aria-expanded={cameraOpen}
					disabled={disabled}
					onClick={() => setCameraOpen((open) => !open)}
				>
					{cameraOpen ? <X /> : <Camera />}
				</Button>
			</form>
			{cameraOpen &&
				camera({ onResult: submit, onClose: () => setCameraOpen(false) })}
		</div>
	)
}
